/**
 * `/internal/ops/pricing` + `/internal/ops/fx-rate-samples` contract types.
 *
 * Both surfaces live on platform-api (owner of `exchange_rate_policy` +
 * `market_rate_cache`, migrations 0012 + 0013). Callers:
 *
 *   - `/internal/ops/pricing` — ops-api (role-gated operator read + write),
 *     proxied from `/v1/ops/pricing` in the operator console.
 *   - `/internal/ops/fx-rate-samples` — control-loops-worker's
 *     `fx_rate_refresh` cron (every 6h). Bulk-append samples observed from
 *     Frankfurter + exchangerate.host. Never called from operator paths.
 *
 * Epic #84 splits the two rates deliberately:
 *
 *   - `exchange_rate_policy.cny_per_usd` is the **topup conversion rate**
 *     (`R`), manually set by ops. It's the contractual rate a customer pays
 *     to receive USD balance (¥`R` = $1). Audit trail is mandated by the
 *     epic because L1 margin is booked at record time against this rate.
 *
 *   - `market_rate_cache` is the **observed market rate** (Frankfurter /
 *     exchangerate.host). It never drives customer-facing pricing — only
 *     display / L2 reference. revenue_ledger snapshots the market rate per
 *     row at record time so historical L2 is reproducible.
 */

// ─── Fx-rate sample ingest (cron → platform-api) ────────────────────────

/**
 * One observed market-rate sample. `source` is advisory-free-form on the
 * wire (matches `market_rate_cache.source` TEXT column precedent) — the
 * cron only writes `'frankfurter'` or `'exchangerate_host'` today, but the
 * DB column has no CHECK so new sources require no schema change.
 *
 * `rate` is a REAL number (e.g. `7.2034` for CNY/USD). `sampled_at` is the
 * observation time (ISO-8601), not the insert time — though the cron
 * writes them simultaneously, the distinction matters for replayed fills
 * in future.
 */
export interface FxRateSample {
  readonly source: string;
  readonly pair: string;
  readonly rate: number;
  readonly sampled_at: string;
}

/**
 * `POST /internal/ops/fx-rate-samples` body.
 *
 * Bulk-append endpoint. Each sample is validated per-row; a mixed batch
 * with one bad row rejects the entire batch so the cron can retry with
 * confidence. `inserted` in the response counts rows that landed.
 */
export interface FxRateSamplesRequest {
  readonly samples: readonly FxRateSample[];
}

export interface FxRateSamplesResponse {
  readonly ok: true;
  readonly inserted: number;
}

// ─── Exchange-rate policy (ops CRUD) ────────────────────────────────────

/**
 * One row from `exchange_rate_policy`. Phase 4 ships only `pair='USD_CNY'`,
 * so callers always see exactly one row; the contract models the table
 * shape to leave multi-pair expansion trivial.
 *
 * `updated_by` is a soft ref to `ops_users.id`; no FK per migration 0012
 * (disabled ops users may persist while the rate row survives). The
 * operator console renders it as-is; tests assert it echoes the
 * `X-Ops-User-Id` header from the ops session.
 */
export interface ExchangeRatePolicyRecord {
  readonly pair: string;
  readonly cny_per_usd: number;
  readonly updated_at: string;
  readonly updated_by: string;
  readonly note: string | null;
}

/**
 * One row from `market_rate_cache` — the most recent sample for the pair.
 * Returned alongside the policy so the ops console can display
 * "market reference" + "spread vs market" in-page without a second fetch.
 *
 * `null` when no sample has ever been observed (e.g. first boot before
 * the cron has run).
 */
export interface MarketRateRecord {
  readonly source: string;
  readonly pair: string;
  readonly rate: number;
  readonly sampled_at: string;
}

/**
 * `GET /internal/ops/pricing` + `GET /v1/ops/pricing` response.
 *
 * Returns the USD_CNY policy row plus the most recent market sample for
 * the same pair. `policy` is `null` when ops has never set a rate (fresh
 * deploy before the first save) — the console renders an empty form.
 * `latest_market` is `null` when no cron has populated a sample yet.
 */
export interface GetPricingResponse {
  readonly policy: ExchangeRatePolicyRecord | null;
  readonly latest_market: MarketRateRecord | null;
}

/**
 * `POST /internal/ops/pricing` / `POST /v1/ops/pricing` body.
 *
 * Upserts the single `exchange_rate_policy` row for `pair='USD_CNY'`.
 *
 *   - `cny_per_usd` must be `>0` and `<=100`. The upper bound catches
 *     typo-grade mistakes (72 vs 7.2) without being so tight that it
 *     breaks on future volatility. Bounds are enforced on the server;
 *     the ops-api proxy does NOT re-validate (single source of truth).
 *   - `note` is optional free-form context explaining the rate change.
 *     Stored verbatim in `exchange_rate_policy.note`.
 *
 * Server fills `updated_at = CURRENT_TIMESTAMP` and `updated_by =
 * X-Ops-User-Id` header. Ops-api injects the header from the session
 * cookie; client-supplied `updated_by` is deliberately absent from the
 * contract so spoofing the actor would require crafting a forged ops
 * session, not just editing a JSON field.
 */
export interface UpsertPricingRequest {
  readonly cny_per_usd: number;
  readonly note?: string;
}

export interface UpsertPricingResponse {
  readonly policy: ExchangeRatePolicyRecord;
}

/**
 * Server-side validation bounds for `cny_per_usd` on
 * `POST /internal/ops/pricing`.
 *
 *   - `CNY_PER_USD_MIN` is a strict lower bound (`rate > MIN` required).
 *     Kept at `0` because a zero or negative FX rate is nonsense; the
 *     contract sanity-checks against it rather than silently accepting.
 *   - `CNY_PER_USD_MAX` is an inclusive upper bound (`rate <= MAX`
 *     accepted). Kept at `100` to catch decimal-point typos (72 vs 7.2)
 *     without being so tight that future volatility blows through it.
 *
 * Shared from the contracts package so the platform-api handler, the
 * ops-console inline form validator, and any future client-side tool
 * all point at the same numbers. If the bounds change, one edit here
 * is the only place that needs to move.
 */
export const CNY_PER_USD_MIN = 0;
export const CNY_PER_USD_MAX = 100;
